import type { ErrorInfo, ReactNode } from 'react';
import type { StudentLoansInlineProps } from '@highereducation/hen-ui';
import LeadDelivery from '@de/types/leadDelivery';
import type {
  CustomDimensionMapping,
  ProductFieldMapping,
} from '@de/utils/configurable-step/productEventMappingContract';
import {
  ConditionType,
  LogicType,
  Operator,
  QuestionOrientationType,
  QuestionType,
  ResultType,
  StepType,
} from './enums';

export type CustomDimensions = Array<{ key: string; value: string }>;

/**
 * Lead type definition for form submissions
 */
export type Lead = {
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  zip?: string;
  countryCode?: string;
  hsGradYear?: string;
  state?: string;
  city?: string;
  address?: string;
  levelOfEducation?: string;
  degree?: string;
  category?: string;
  subject?: string;
  militaryStatus?: string;
  nursingLicense?: string;
  teachingCredential?: string;
  startDate?: string;
  rnLicense?: string;
  enrollmentTimeline?: string;
  workExperience?: string;
  preferredContactMethod?: string;
  emailConsent?: string;
  birthDate?: string;
  gender?: string;
  gpa?: string;
  intendedStartDate?: string;
  bestTimeToContact?: string;
  currentOccupation?: string;
  employer?: string;
  campusPreference?: string;
  financialAid?: string;
  programFormat?: string;
  [key: string]: string | undefined;
};

export interface StatisticHighlightConfig {
  title: string;
  statistic: string;
  description: string;
  sourceText?: string;
}

export interface TextBoxConfig {
  title: string;
  text: string;
}

export interface GraphicCardConfig {
  text: string;
  graphic?: 'horizontal' | 'vertical';
  itemNameOne?: string;
  itemNameTwo?: string;
  sourceText?: string;
}

export type SideRailConfigItem = {
  id: string;
  type: 'textBox' | 'statisticHighlight' | 'graphicCard';
  config: StatisticHighlightConfig | TextBoxConfig | GraphicCardConfig;
};

export type DecisionProfile = {
  career: string;
  degree: string;
  subject: string;
  decisionFactor: string;
};

export type DecisionProfileResponse = {
  careerDecisionOutcome?: string;
  careerDecisionStatus?: string;
  degreeDecisionOutcome?: string;
  degreeDecisionStatus?: string;
  inferredExperience: string;
  primaryDecisionFactor?: string;
  primaryDecisionSubfactors?: string[];
  schoolDecisionStatus?: string;
  subjectDecisionOutcome?: string;
  subjectDecisionStatus?: string;
  careerDecisionOutcomeCode?: string[];
};

/**
 * Resolved DSL (Domain Specific Language) configuration payload.
 * Populated server-side on `Component` or `StepResult` steps that reference a
 * published DSL config via `dslConfigId`. When present, the step is rendered
 * via the generic DSL renderer instead of its `componentType`/`resultType`.
 */
export type DslConfigPayload = {
  configId: string;
  name: string;
  status: string;
  type: string;
  version: number;
  data: Record<string, unknown>;
  componentCount?: number;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
  tenantId?: string;
  publishedBy?: string;
  origin?: string;
};

/**
 * Entry-point tracking config block declared on the DSL config root
 * (`data.root.props.tracking`).
 *
 * `productFields` is a dual-shape map of
 * `<wireProductField> -> string | { dataPath } | { literal }`.
 * Legacy strings are path mappings. `{ literal }` is valid only on
 * `formatType` / `formatSubtype`. The resolver takes a caller-supplied
 * root (Entry `flowContext` or EL `item`).
 *
 * `customDimensions` is a sibling array on the same `tracking` block
 * (never nested inside a productFields value, never a
 * `tracking.productEvents` tree). Each entry is `{ key, dataPath }`
 * or `{ key, literal }`.
 *
 * At least one of `productFields` / `customDimensions` is present:
 * `extractTrackingConfig` returns `null` when the DSL omits the block
 * or sanitization drops every entry.
 *
 * Example:
 * ```jsonc
 * {
 *   "tracking": {
 *     "productFields": {
 *       "variant":  "degree",
 *       "name":     { "dataPath": "subject" },
 *       "formatSubtype": { "literal": "dexp-entry" },
 *       "brand":    "schoolSlug"
 *     },
 *     "customDimensions": [
 *       { "key": "partner", "literal": "edx" }
 *     ]
 *   }
 * }
 * ```
 */
export interface EntryPointTracking {
  productFields?: Record<string, ProductFieldMapping>;
  customDimensions?: readonly CustomDimensionMapping[];
}

export interface ResultsPageModalConfig {
  enabled?: boolean;
  title?: string;
  subtitle?: string;
  disclosureTitle?: string;
  disclosureContent?: string;
}

export type ResultsPageComparisonVariant = 'POPUP';

export interface ResultsPageComparisonAttribute {
  label: string;
  key: string;
}

export interface ResultsPageComparisonConfig {
  enabled?: boolean;
  minSelection?: number;
  variant?: ResultsPageComparisonVariant;
  bannerText?: string;
  ctaText?: string;
  heading?: string;
  logo?: string;
  title?: string;
  subtitle?: string;
  buttonText?: string;
  placeholderText?: string;
  compareAttributes?: ResultsPageComparisonAttribute[];
  maxSelection?: number;
}

export interface ResultsPageSubmissionsConfig {
  enabled?: boolean;
  title?: string;
  subtitle?: string;
}

export interface ResultsPageLoadingStepConfig {
  label: string;
  icon?: 'compare' | 'checklist' | 'apply';
}

export interface ResultsPageLoadingConfig {
  enabled?: boolean;
  title?: string;
  statusLabel?: string;
  steps?: ResultsPageLoadingStepConfig[];
}

export interface StudentLoansInlineDslContent {
  type: 'StudentLoansInline';
  props?: Record<string, unknown>;
}

export type ResultsPageExitStrategyStudentLoansInlineConfig = {
  enabled?: boolean;
} & Partial<
  Pick<
    StudentLoansInlineProps,
    'title' | 'subtitle' | 'disclosure' | 'partner' | 'cta'
  >
>;

export interface ResultsPageExitStrategyExtraCtaConfig {
  enabled?: boolean;
  label?: string;
  eventName?: string;
}

export interface ResultsPageExitStrategyThankYouConfig {
  enabled?: boolean;
}

export interface ResultsPageExitStrategyConfig {
  thankYou?: ResultsPageExitStrategyThankYouConfig;
  studentLoansInline?: ResultsPageExitStrategyStudentLoansInlineConfig;
  extraCta?: ResultsPageExitStrategyExtraCtaConfig;
}

export interface ResultsPageConfig {
  flowType?: string;
  recommender?: {
    modal?: ResultsPageModalConfig;
  };
  spotlight?: {
    modal?: ResultsPageModalConfig;
  };
  comparison?: ResultsPageComparisonConfig;
  submissions?: ResultsPageSubmissionsConfig;
  exitStrategy?: ResultsPageExitStrategyConfig;
  loading?: ResultsPageLoadingConfig;
}

export type ResultsPageModalCopy = Pick<
  ResultsPageModalConfig,
  'title' | 'subtitle' | 'disclosureTitle' | 'disclosureContent'
>;

export const DEFAULT_RESULTS_PAGE_CONFIG: ResultsPageConfig = Object.freeze(
  {},
) as ResultsPageConfig;

export const getDefaultResultsPageConfig = (): ResultsPageConfig =>
  DEFAULT_RESULTS_PAGE_CONFIG;

/**
 * The DCS triad an entry-point config can pre-seed. Each slug is optional —
 * authors fill only the dimensions they want a default for.
 */
export interface EntryPointDcsDefaults {
  degree?: string;
  category?: string;
  subject?: string;
}

/**
 * `data.root.props.entryPoint` — the entry-point analog of
 * {@link ResultsPageConfig}. Only valid on `type: "entry"` DSL configs
 * (enforced by the dynamic-flows Layer 1 validator).
 *
 * Lets an author declare default DCS values for the flow's entry step:
 *
 * - **Seed time** — fills any DCS dimension the host did NOT provide
 *   (host context always wins; unset dims stay unseeded).
 * - **Advance/submit commit** — fills blank triad dims the user left
 *   unselected (Select placeholders) into flowState options before
 *   branching / eject URL / DCS product reads. Does not re-paint DSM
 *   formState, so Selects keep their placeholders.
 *
 * `enabled` is the master toggle: when falsy the defaults are ignored
 * entirely for both lifecycles.
 *
 * `subjectLabelFallback` is the author-owned fallback for
 * `{{context.subject_label_with_fallback}}` when the page subject is
 * missing or the general sentinel — set per domain (never hardcoded into
 * platform taxonomy). Suitable for headings and other copy surfaces.
 */
export interface EntryPointConfig {
  enabled?: boolean;
  defaults?: EntryPointDcsDefaults;
  /**
   * Opt-in allowlist of non-PII `customField` handles to forward on
   * Discover eject as `ctx_*` query params. Omitted / undefined leaves
   * the feature off.
   */
  forwardFields?: string[];
  /**
   * Author-owned fallback for `context.subject_label_with_fallback` when
   * `context.subject` is missing or `general`. Example (Psychology):
   * `"Higher Education"` so
   * `Change Lives with a {{context.subject_label_with_fallback}} Degree`
   * completes on general/default pages.
   */
  subjectLabelFallback?: string;
}

export type Component = {
  componentType?: string;
  redirectUrl?: string;
  dslConfigId?: string;
  dslConfig?: DslConfigPayload;
};

/**
 * UI shown in place of a DSL content tree that threw while rendering. The
 * function form receives the raw error and, from the second render on, the
 * component stack. Intended for internal authoring surfaces, not public pages.
 */
export type DslRenderErrorFallback =
  | ReactNode
  | ((error: Error, info?: ErrorInfo) => ReactNode);

/**
 * Called with the raw error and component stack when a DSL content tree
 * throws. Intended for internal authoring surfaces, not public pages.
 */
export type DslRenderErrorHandler = (error: Error, info: ErrorInfo) => void;

/**
 * Props accepted by the `ConfigurableStep` bridge variant. The bridge
 * is the convergence point between the DE flow engine and the data
 * binding stack (child SAD ADR-15) -- it consumes the same resolved
 * `dslConfig` payload as `DslConfig` and wraps the render tree in the
 * S8 minimum provider stack (DSM + ActionDispatcher + Puck Render).
 */
export type ConfigurableStepProps = {
  dslConfig?: DslConfigPayload;
  flowType?: string;
  trafficSource?: string;
  /**
   * Replaces only the DSL content tree, not the surrounding step; errors
   * outside it still reach the host's boundaries. Supplying it also clears the
   * error when the config changes, so keep `dslConfig` referentially stable.
   * Exposes the raw error, so use it on internal surfaces such as the Preview.
   */
  renderErrorFallback?: DslRenderErrorFallback;
  /**
   * Receives the raw error and component stack when the DSL content tree
   * throws. Meant for internal surfaces such as the Preview, not public pages.
   */
  onRenderError?: DslRenderErrorHandler;
  /**
   * Host-only. When `true`, `lead.submit` validates and writes its status
   * keys and runs `onSuccess`, but sends no request. Set by Admin Preview so
   * authors can test a form without posting a real lead. Absent or `false`
   * sends. Not a DSL param: authored config cannot set it.
   *
   * Applies only to a `ConfigurableStep` the host mounts directly. A step
   * rendered through a flow (`Results`, `ComponentRenderer`) does not receive
   * it and always sends.
   */
  leadSubmitDryRun?: boolean;
};

export type Option = {
  optionId: string;
  text: string;
  group?: string;
  value?: string | boolean | null;
  /**
   * Canonical slug for this selection (e.g. `"bachelors"`,
   * `"nursing-and-healthcare"`). Populated by the DSL bridge when the
   * bound data source carries a `slug` field on each item — the
   * bridge looks the slug up from the source by matching
   * `_bind.optionValueField` against the picked `value`.
   *
   * Decoupled from `value` on purpose: DSL authors bind
   * `optionValueField` to the field downstream **cascading queries**
   * need (Mobius's `criteria.degreeId` is an int64, so the value has
   * to be the numeric `id`), while DSL **branching conditions**
   * (`COMBINATION_SELECTED`) are keyed on slugs. Carrying both keeps
   * `formState[formKey]` compatible with templated source bodies and
   * lets `evaluateCondition` resolve slug-based combinations without
   * a hard-coded slug list or a brittle id-based condition tree.
   */
  slug?: string;
  nextQuestionId?: string;
  questionId?: string;
  leadFieldAssociated?: string;
  /**
   * Author-declared `_meta.customField` handle for this specific option,
   * distinct from `leadFieldAssociated`. Used by multi-`formKey`
   * Configurable Steps (e.g. an entry step with several dropdowns under
   * one step id) to opt a single dropdown into `entryPoint.forwardFields`
   * allowlisting without conflating it with Lead-submission field mapping.
   * See `buildContextParams` / `getOptionByCustomField`.
   */
  customField?: string;
  isPreSelected?: boolean;
  isArray?: boolean;
  icon?: string;
  subtext?: string;
};

export type StepResponseData = Option[] | Record<string, Option[]>;

export type Placeholder = {
  description: string;
  key: string;
  defaultValue: string;
};

export type QuestionLayout = {
  questionId: string;
  orientation: QuestionOrientationType;
};

export type GroupedQuestions = {
  questions: Question[];
  indices: number[];
};

export type BranchingLogic = {
  type: LogicType;
  nextId: string;
  condition: {
    type: ConditionType;
    operator?: Operator;
    optionId?: string;
    value?: string;
    group?: string;
    combination?: string[];
    optionIds?: string[];
  };
};

export type MultiQuestion = {
  questions?: QuestionLayout[];
  stepQuestions?: Question[];
  groupedQuestions?: GroupedQuestions[];
  bulletPoints?: string[];
  enableDivider?: boolean;
  sideRailConfigs?: SideRailConfigItem[];
  required?: boolean;
  automaticNext?: boolean;
  tcpaText?: string;
};

export interface OptionConfig {
  optionId: string;
  text: string;
  value: string;
  extractKeys?: boolean;
}

export interface CompareAttribute {
  label?: string;
  key?: string;
}

export interface CompareConfig {
  enableCompare?: boolean;
  variant?: ResultsPageComparisonVariant;
  maxSelection?: number;
  minSelection?: number;
  logo?: string;
  heading?: string;
  title?: string;
  subtitle?: string;
  bannerText?: string;
  ctaText?: string;
  buttonText?: string;
  placeholderText?: string;
  compareAttributes?: CompareAttribute[];
}
export interface CompareAttributeValue {
  label?: string;
  key?: string;
  value: string;
}
export interface ResultsConfig {
  subHeading?: string;
  title?: string;
  subtitle?: string;
  image?: string;
  ctaText?: string;
  logo?: string;
  redirectUrl?: string;
  heading?: string;
  stats?: StatsItem[];
  compareConfig?: CompareConfig;
  compareAttributeValues?: CompareAttributeValue[];
}

export interface StatsItem {
  icon?: string;
  text?: string;
}

export type CustomResult = {
  subHeading?: string;
  title?: string;
  subtitle?: string;
  description?: string;
  image?: string;
  ctaText?: string;
  redirectUrl?: string;
  stats?: StatsItem[];
  compareConfig?: CompareConfig;
};

export interface SortConfig {
  enabled: boolean;
  weaverModel?: 'legacy' | 'fyp';
}

export interface DynamicOptions {
  method?: 'GET' | 'POST';
  body?: unknown;
  endpointUrl?: string;
  queryParams?: string[];
  limit?: number;
  searchBarOptions?: boolean;
  optionConfig?: OptionConfig;
  resultsConfig?: ResultsConfig;
  paginationConfig?: PaginationConfig;
  sortConfig?: SortConfig;
}

export enum InfoBannerIcon {
  INFO = 'info',
  WARNING = 'warning',
}

export interface InfoBannerProps {
  title: string;
  message: string;
  icon: InfoBannerIcon;
}

export interface InfoBanner {
  id: string;
  type: 'infoBanner';
  config: InfoBannerProps;
  condition?: BranchingLogic['condition'];
}

export type Question = {
  infoBannerConfigs?: InfoBanner[];
  options: Option[];
  dynamicOptions?: DynamicOptions;
  questionType: QuestionType;
  id: string;
  type: StepType;
  title?: string;
  subtitle?: string;
  tooltip?: string;
  leadFieldAssociated?: keyof Lead;
  customField?: string;
  branchingLogic?: BranchingLogic[];
  placeholders?: Placeholder[];
  placeholderText?: string;
  orientation?: QuestionOrientationType;
  sideRailConfigs?: SideRailConfigItem[];
  required?: boolean;
  maxSelections?: number;
  automaticNext?: boolean;
  tcpaText?: string;
  dynamicOptionsFilters?: {
    type?: string;
    subject?: string;
    filterKey?: 'types' | 'subTypes' | 'subjects' | 'skills';
  };
};

export type Modal = {
  title?: string;
  content?: string;
  buttonText?: string;
  enable?: boolean;
};

export type DCS = {
  bulletPoints?: string[];
  questions?: QuestionLayout[];
  enableDivider?: boolean;
  redirectUrl?: string;
  stepQuestions?: Question[];
  groupedQuestions?: GroupedQuestions[];
  image?: string;
  description?: string;
  ctaText?: string;
  title?: string;
  subtitle?: string;
  variant?: string;
};

export type ExternalLink = {
  url: string;
};

export type BannerCTA = {
  title: string;
  subtitle?: string;
  image?: string;
  ctaText?: string;
};

export type Step = {
  customField?: string;
  /** DE-owned enum; drives flow-type budget restriction matching downstream. */
  flowType?: string;
  /** DE-owned enum; drives traffic-source budget restriction matching downstream. */
  trafficSource?: string;
  monetizationChannel?: string;
  flowId?: string;
  isEntry?: boolean;
  id?: string;
  type: StepType;
  title?: string;
  subtitle?: string;
  preSubHeading?: string;
  leadFieldAssociated?: keyof Lead;
  branchingLogic?: BranchingLogic[];
  placeholders?: Placeholder[];
  tooltip?: string;
  modal?: Modal;
  isTerminal: boolean;
  required?: boolean;
  stepName?: string;
  nextButtonText?: string;
} & (
  | DCS
  | ExternalLink
  | Question
  | MultiQuestion
  | Component
  | StepResult
  | BannerCTA
);

export type Flow = Step[];

export type Location = {
  postalCode?: string;
  city?: string;
  state?: string;
  country?: string;
};

export type ContactRequest = {
  degree: string;
  subject: string;
  category: string;
  firstName: Lead['firstName'];
  lastName: Lead['lastName'];
  email: Lead['email'];
  phoneNumber: Lead['phoneNumber'];
  zip: Location['postalCode'];
  details: LeadDelivery;
  cohesionSessionId: string;
  trackingContext?: {
    publisher: string;
    [key: string]: unknown;
  };
};

export type SaveContactParams = {
  degree: ContactRequest['degree'];
  subject: ContactRequest['subject'];
  category: ContactRequest['category'];
  cohesionSessionId: ContactRequest['cohesionSessionId'];
  lead: ContactRequest['details'];
  publisher: string;
};

export interface SaveContactApiResponseSuccess {
  contactId: string;
  transactionId: number;
  retrievalUrl: string;
}

export type SaveContactApiResponseFailure = {
  code: string;
  error: string;
  path: string;
};

export type SaveContactResult = {
  success: boolean;
  data?: SaveContactApiResponseSuccess;
  error?: SaveContactApiResponseFailure;
};

export enum ResultVariant {
  DEFAULT_UNDERGRAD = 'DEFAULT_UNDERGRAD',
  READY_TO_GO = 'READY_TO_GO',
}

/** Section config for custom result steps (useCustomResultsPage). */
export interface ResultSection {
  title?: string;
  subtitle?: string;
  dynamicContent?: DynamicOptions;
  layout?: {
    type?: 'vertical' | 'horizontal';
    itemCount?: number;
  };
}

export type StepResult = {
  resultType?: ResultType;
  programContext: ProgramContext;
  redirectUrl?: string;
  variant?: ResultVariant;
  recommendationFilters?: {
    subjects?: string;
  };
  sections?: ResultSection[];
  title?: string;
  subtitle?: string;
  dslConfigId?: string;
  dslConfig?: DslConfigPayload;
};

export type ProgramContext = {
  subject?: {
    name: string;
  };
  category?: {
    name: string;
  };
  degree?: {
    name: string;
  };
};

/**
 * SlotPlacementId: is the placement/slot where the component is going to render.
 */
export type SlotPlacementId =
  | 'aboveTheFold'
  | 'belowTheFold'
  | 'sidebar'
  | 'bottomOfPage'
  | 'el'
  | 'dashboard'
  | 'personalizedQdf'
  | 'inContent'
  | 'programFinder'
  | '';

export type SlotPlacementData = {
  flowId: string;
  [key: string]: unknown;
};

/**
 * DCS (Degree, Category, Subject) context for forms
 */
export type DCSContext = {
  degree: string;
  category: string;
  subject: string;
};

/**
 * Page-authored headline copy supplied by the host (e.g. extracted from a
 * legacy sonic tag's light DOM by the affiliate adapter). Exposed to DSL
 * configs as `{{context.headline.*}}` so an entry step's heading can adopt
 * the host page's copy instead of a static default. `prefix`/`emphasis`/
 * `suffix` carry the two-tone split; `emphasis` is the whole headline when
 * the host copy has no emphasized segment. Invariant:
 * `text === prefix + emphasis + suffix`.
 *
 * Duplicated as `HostHeadline` in the affiliate bundle
 * (`packages/affiliate-bundle/src/adapter/declarative/translateHostTag.ts`)
 * so the adapter stays dependency-free — keep the shapes in sync.
 */
export type HostHeadline = {
  text: string;
  prefix: string;
  emphasis: string;
  suffix: string;
};

/**
 * A single value in the {@link FlowContextParams} bag. Scalars cover the
 * common case (path segments, flags, counts); the array forms let a
 * placement hand a typed list to a DSL data-source body — e.g. a rankings
 * config forwarding `sponsoredPositions: number[]`. A single-expression
 * `{{context.params.foo}}` template preserves the raw typed value (array
 * included) for body/query serialization; a mixed template comma-joins
 * arrays and stringifies scalars.
 */
export type FlowContextParamValue =
  | string
  | number
  | boolean
  | number[]
  | string[];

/**
 * Generic per-instance key/values a placement can hand to a reusable /
 * parameterized DSL. Surfaced as the interpolation root
 * `{{context.params.*}}` (and `_bind` `{ source: 'context', path:
 * 'params.*' }`) so a single DSL can resolve placement-scoped values that
 * are not DCS dimensions — e.g. a rankings config resolving
 * `"/v1/rankings/{{context.params.listKey}}"` (string) or a param-driven
 * `sponsoredPositions` (number[]).
 */
export type FlowContextParams = Record<string, FlowContextParamValue>;

export interface PaginationConfig {
  enabled?: boolean;
}

export interface PaginationData {
  total: number;
  limit: number;
  offset: number;
  totalCount?: number;
}
